import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Modal,
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
    useWindowDimensions,
    Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import Observables from '@happy-gastro/global-observables';
import { BRAND, useManagementTheme } from '../helpers/managementTheme';
import changelog from './changelog.json';
import pkg from '../../package.json';

const isNewerVersion = (current, lastSeen) => {
    if (!current) return false;
    if (!lastSeen) return false;
    const curParts = current.split('.').map(Number);
    const seenParts = lastSeen.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
        const cur = curParts[i] || 0;
        const seen = seenParts[i] || 0;
        if (cur > seen) return true;
        if (cur < seen) return false;
    }
    return false;
};

const GROUP_META = {
    feature: {
        label: { en: "New features", hu: "Új funkciók", de: "Neue Funktionen", cn: "新功能", ro: "Funcții noi", svn: "Nove funkcije" },
        color: BRAND.base,
        icon: "rocket-launch",
    },
    improvement: {
        label: { en: "Improvements", hu: "Fejlesztések", de: "Verbesserungen", cn: "改进", ro: "Îmbunătățiri", svn: "Izboljšave" },
        color: "#2563eb",
        icon: "trending-up",
    },
    fix: {
        label: { en: "Fixes", hu: "Javítások", de: "Fehlerbehebungen", cn: "修复", ro: "Remedieri", svn: "Popravki" },
        color: "#10b981",
        icon: "bug",
    },
    security: {
        label: { en: "Security", hu: "Biztonság", de: "Sicherheit", cn: "安全", ro: "Securitate", svn: "Varnost" },
        color: "#f59e0b",
        icon: "shield-check",
    },
    performance: {
        label: { en: "Performance", hu: "Teljesítmény", de: "Leistung", cn: "性能", ro: "Performanță", svn: "Zmogljivost" },
        color: "#8b5cf6",
        icon: "speedometer",
    },
    other: {
        label: { en: "Other", hu: "Egyéb", de: "Sonstiges", cn: "其他", ro: "Altele", svn: "Drugo" },
        color: "#6b7280",
        icon: "lightbulb-on",
    },
    // Internal / developer-only. Items (or whole groups) with audience:"internal"
    // are hidden from everyone except system:admin (see filterReleasesForAudience).
    internal: {
        label: { en: "Developer", hu: "Fejlesztői", de: "Entwickler", cn: "开发者", ro: "Dezvoltator", svn: "Razvijalec" },
        color: "#8b5cf6",
        icon: "code-tags",
    },
};

/** True when a changelog item or group is flagged internal (developer-only). */
const isInternalEntry = (entry) => String(entry?.audience || "").toLowerCase() === "internal";

/**
 * Strip internal-only content unless the viewer is system:admin: drop internal
 * items, then empty groups, then empty releases — so a customer never sees a
 * blank dev-only release card.
 */
const filterReleasesForAudience = (list, includeInternal) => {
    if (includeInternal) return Array.isArray(list) ? list : [];
    return (Array.isArray(list) ? list : [])
        .map((rel) => {
            const groups = (Array.isArray(rel?.groups) ? rel.groups : [])
                .map((g) => {
                    if (isInternalEntry(g)) return null;
                    const items = (Array.isArray(g?.items) ? g.items : []).filter((it) => !isInternalEntry(it));
                    return items.length ? { ...g, items } : null;
                })
                .filter(Boolean);
            return { ...rel, groups };
        })
        .filter((rel) => (rel.groups || []).length > 0);
};

/** Collect every role string off a stored user object (roles/effective/default). */
const collectUserRoleStrings = (user) => {
    const out = [];
    const add = (v) => {
        if (Array.isArray(v)) v.forEach(add);
        else if (v) out.push(String(v));
    };
    add(user?.roles);
    add(user?.effectiveRoles);
    add(user?.defaultRoles);
    return out;
};

const LOCALES = {
    en: {
        title: "What's new",
        subtitle: "Discover the latest improvements in Happy App",
        latest: "Latest",
        released_on: "Released on {{date}}",
        got_it: "Got it",
        close: "Close",
        no_changes: "No release notes available yet.",
    },
    hu: {
        title: "Újdonságok",
        subtitle: "Fedezd fel a Happy App legújabb fejlesztéseit",
        latest: "Legújabb",
        released_on: "Kiadva: {{date}}",
        got_it: "Értem",
        close: "Bezárás",
        no_changes: "Még nincsenek elérhető újdonságok.",
    },
    de: {
        title: "Was ist neu",
        subtitle: "Entdecke die neuesten Verbesserungen in der Happy App",
        latest: "Neueste",
        released_on: "Veröffentlicht am {{date}}",
        got_it: "Verstanden",
        close: "Schließen",
        no_changes: "Noch keine Versionshinweise verfügbar.",
    },
    cn: {
        title: "新功能",
        subtitle: "了解 Happy App 的最新改进",
        latest: "最新",
        released_on: "发布于 {{date}}",
        got_it: "知道了",
        close: "关闭",
        no_changes: "暂无版本更新说明。",
    },
    ro: {
        title: "Noutăți",
        subtitle: "Descoperă cele mai recente îmbunătățiri din Happy App",
        latest: "Cea mai recentă",
        released_on: "Lansat pe {{date}}",
        got_it: "Am înțeles",
        close: "Închide",
        no_changes: "Încă nu există note de lansare.",
    },
    svn: {
        title: "Kaj je novega",
        subtitle: "Odkrij najnovejše izboljšave v aplikaciji Happy",
        latest: "Najnovejše",
        released_on: "Izdano {{date}}",
        got_it: "Razumem",
        close: "Zapri",
        no_changes: "Opomb ob izdaji še ni.",
    }
};

const localizeField = (field, lang) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    const base = (lang || "en").split("-")[0].toLowerCase();
    return field[base] || field.en || Object.values(field).find(Boolean) || "";
};

// Map our app language codes (incl. non-standard "cn"/"svn") to BCP-47 locales
// so Intl date formatting renders in the user's language instead of falling back.
const DATE_LOCALE_MAP = {
    en: "en-US",
    hu: "hu-HU",
    de: "de-DE",
    cn: "zh-CN",
    ro: "ro-RO",
    svn: "sl-SI",
};

const formatReleaseDate = (dateStr, lang) => {
    if (!dateStr) return "";
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return dateStr;
    const base = (lang || "hu").split("-")[0].toLowerCase();
    const intlLocale = DATE_LOCALE_MAP[base] || "hu-HU";
    try {
        return parsed.toLocaleDateString(intlLocale, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch (_e) {
        return parsed.toLocaleDateString();
    }
};

/**
 * "What's new" release-notes modal: a SOLID white / navy card (radius 28) with an
 * orange gift badge, expandable release cards and a pill "Got it" button. Opens on
 * the `openWhatsNew` observable and automatically after a version upgrade.
 *
 * @param {object} props
 * @param {boolean} props.isLoggedIn Whether a user session exists.
 * @param {boolean} [props.isAuthReady=true] Whether auth bootstrap has finished.
 * @returns {?JSX.Element} The modal while visible, otherwise `null`.
 */
const WhatsNewModal = ({ isLoggedIn, isAuthReady = true }) => {
    const { i18n } = useTranslation();
    const m = useManagementTheme();
    const styles = useMemo(() => StyleSheet.create(buildStyles(m)), [m]);
    const [visible, setVisible] = useState(false);
    const [releases, setReleases] = useState([]);
    const [expandedReleases, setExpandedReleases] = useState({});
    // Internal (developer-only) changelog entries are shown only to system:admin.
    const [includeInternal, setIncludeInternal] = useState(false);

    const activeLang = i18n.language || "hu";
    const dict = LOCALES[activeLang.split("-")[0]] || LOCALES.en;

    const currentVersion = pkg.version || "0.0.0";

    // load releases
    useEffect(() => {
        if (changelog && Array.isArray(changelog.releases)) {
            setReleases(changelog.releases);
            // Default expanded first release
            if (changelog.releases.length > 0) {
                setExpandedReleases({ [changelog.releases[0].version]: true });
            }
        }
    }, []);

    // Resolve whether the current user may see internal entries (system:admin),
    // reading the stored user object. Best-effort — defaults to hidden.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const raw = (await AsyncStorage.getItem("user")) || (await AsyncStorage.getItem("lastUser"));
                if (!raw) return;
                const roles = collectUserRoleStrings(JSON.parse(raw));
                if (alive) setIncludeInternal(roles.includes("system:admin"));
            } catch (_e) {
                // Non-fatal: keep internal entries hidden.
            }
        })();
        return () => {
            alive = false;
        };
    }, [isLoggedIn]);

    // Releases with internal-only content stripped for non-admins.
    const visibleReleases = React.useMemo(
        () => filterReleasesForAudience(releases, includeInternal),
        [releases, includeInternal],
    );

    const markAsSeen = useCallback(async () => {
        try {
            await AsyncStorage.setItem("whatsNewLastSeenVersion", currentVersion);
        } catch (e) {
            console.error("Failed to save whatsNewLastSeenVersion", e);
        }
    }, [currentVersion]);

    const handleClose = useCallback(() => {
        setVisible(false);
        markAsSeen();
    }, [markAsSeen]);

    // Listen to global open event
    useEffect(() => {
        const onOpen = () => {
            if (!isLoggedIn || !isAuthReady) return;
            setVisible(true);
        };
        Observables.register("openWhatsNew", onOpen);
        return () => {
            if (Observables && typeof Observables.remove === "function") {
                Observables.remove("openWhatsNew");
            }
        };
    }, [isLoggedIn, isAuthReady]);

    // Auto-open on login & version upgrade
    useEffect(() => {
        if (!isLoggedIn || !isAuthReady) return;

        const checkVersionUpgrade = async () => {
            try {
                const lastSeen = await AsyncStorage.getItem("whatsNewLastSeenVersion");
                if (!lastSeen) {
                    // First run: treat as seen to not annoy user immediately
                    await AsyncStorage.setItem("whatsNewLastSeenVersion", currentVersion);
                } else if (isNewerVersion(currentVersion, lastSeen)) {
                    // Version upgrade detected! Open modal automatically
                    setVisible(true);
                }
            } catch (e) {
                console.error(e);
            }
        };

        // Delay checking version upgrade slightly so startup completes
        const timer = globalThis.setTimeout(checkVersionUpgrade, 2000);
        return () => globalThis.clearTimeout(timer);
    }, [isLoggedIn, isAuthReady, currentVersion]);

    const toggleExpand = (version) => {
        setExpandedReleases(prev => ({
            ...prev,
            [version]: !prev[version]
        }));
    };

    const { width, height } = useWindowDimensions();
    const modalWidth = width >= 768 ? Math.min(width * 0.8, 700) : width * 0.95;

    if (!visible) return null;

    return (
        <Modal
            animationType="fade"
            transparent={true}
            visible={visible}
            onRequestClose={handleClose}
        >
            <View style={styles.backdrop}>
                <View style={[styles.modalBox, { width: modalWidth, maxHeight: height * 0.85 }]}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerTitleRow}>
                            <View style={styles.celebrationBadge}>
                                <Ionicons name="gift-outline" size={24} color="#FFF" />
                            </View>
                            <View style={{ flex: 1, marginLeft: 12 }}>
                                <Text style={styles.titleText}>{dict.title}</Text>
                                <Text style={styles.subtitleText}>{dict.subtitle}</Text>
                            </View>
                            <TouchableOpacity onPress={handleClose} style={styles.closeBtn}>
                                <Ionicons name="close" size={24} color={m.colors.subText} />
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Content */}
                    <ScrollView style={styles.scrollBody} contentContainerStyle={styles.scrollContent}>
                        {visibleReleases.length === 0 ? (
                            <View style={styles.emptyWrap}>
                                <Text style={styles.emptyText}>{dict.no_changes}</Text>
                            </View>
                        ) : (
                            visibleReleases.map((release, index) => {
                                const isExpanded = expandedReleases[release.version];
                                const groups = Array.isArray(release.groups) ? release.groups : [];
                                const isLatest = index === 0;

                                return (
                                    <View key={release.version || index} style={styles.releaseCard}>
                                        <TouchableOpacity
                                            activeOpacity={0.8}
                                            onPress={() => toggleExpand(release.version)}
                                            style={styles.cardHeader}
                                        >
                                            <View style={styles.cardHeaderLeft}>
                                                <View style={[styles.versionPill, isLatest ? styles.versionPillLatest : {}]}>
                                                    <Text style={[styles.versionText, isLatest ? styles.versionTextLatest : {}]}>
                                                        v{release.version}
                                                    </Text>
                                                </View>
                                                {isLatest && (
                                                    <View style={styles.latestBadge}>
                                                        <Text style={styles.latestText}>{dict.latest}</Text>
                                                    </View>
                                                )}
                                                <Text style={styles.dateText}>
                                                    {dict.released_on.replace("{{date}}", formatReleaseDate(release.date, activeLang))}
                                                </Text>
                                            </View>
                                            <Ionicons
                                                name={isExpanded ? "chevron-up" : "chevron-down"}
                                                size={20}
                                                color={m.colors.muted}
                                            />
                                        </TouchableOpacity>

                                        {isExpanded && (
                                            <View style={styles.cardExpandedContent}>
                                                {groups.map((group, gIdx) => {
                                                    const meta = GROUP_META[group.type] || GROUP_META.other;
                                                    const groupLabel = localizeField(meta.label, activeLang);
                                                    const items = Array.isArray(group.items) ? group.items : [];

                                                    if (items.length === 0) return null;

                                                    return (
                                                        <View key={gIdx} style={styles.groupSection}>
                                                            <View style={styles.groupHeader}>
                                                                <View style={[styles.groupIconBadge, { backgroundColor: meta.color + "15" }]}>
                                                                    <MaterialCommunityIcons name={meta.icon} size={16} color={meta.color} />
                                                                </View>
                                                                <Text style={[styles.groupTitle, { color: meta.color }]}>{groupLabel}</Text>
                                                            </View>

                                                            {items.map((item, itemIdx) => {
                                                                const itemTitle = localizeField(item.title, activeLang);
                                                                const itemDesc = localizeField(item.description, activeLang);

                                                                return (
                                                                    <View key={itemIdx} style={styles.itemRow}>
                                                                        <View style={[styles.dot, { backgroundColor: meta.color }]} />
                                                                        <View style={styles.itemContent}>
                                                                            {itemTitle ? (
                                                                                <Text style={styles.itemTitleText}>{itemTitle}</Text>
                                                                            ) : null}
                                                                            {itemDesc ? (
                                                                                <Text style={styles.itemDescText}>{itemDesc}</Text>
                                                                            ) : null}
                                                                        </View>
                                                                    </View>
                                                                );
                                                            })}
                                                        </View>
                                                    );
                                                })}
                                            </View>
                                        )}
                                    </View>
                                );
                            })
                        )}
                    </ScrollView>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <TouchableOpacity onPress={handleClose} style={styles.gotItBtn}>
                            <Text style={styles.gotItBtnText}>{dict.got_it}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

/**
 * Style factory bound to the management theme (light + navy dark).
 * @param {object} m Result of `useManagementTheme()`.
 * @returns {Object.<string, object>} Style map.
 */
const buildStyles = (m) => ({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(15,23,42,0.55)",
        justifyContent: "center",
        alignItems: "center",
    },
    modalBox: {
        backgroundColor: m.colors.surface,
        borderRadius: 28,
        borderWidth: 1,
        borderColor: m.colors.border,
        overflow: "hidden",
        ...Platform.select({
            ios: {
                shadowColor: "#0F172A",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.25,
                shadowRadius: 24,
            },
            android: {
                elevation: 12,
            },
            web: {
                boxShadow: "0px 18px 40px rgba(15,23,42,0.25)",
            }
        }),
    },
    header: {
        padding: 18,
        borderBottomWidth: 1,
        borderBottomColor: m.colors.border,
        backgroundColor: m.colors.surface,
    },
    headerTitleRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    celebrationBadge: {
        width: 46,
        height: 46,
        borderRadius: 16,
        backgroundColor: BRAND.base,
        justifyContent: "center",
        alignItems: "center",
    },
    titleText: {
        fontSize: 20,
        fontWeight: "800",
        letterSpacing: -0.4,
        color: m.colors.heading,
    },
    subtitleText: {
        fontSize: 12.5,
        color: m.colors.subText,
        marginTop: 2,
    },
    closeBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: m.colors.surfaceAlt,
    },
    scrollBody: {
        backgroundColor: m.colors.surface,
    },
    scrollContent: {
        padding: 16,
    },
    emptyWrap: {
        padding: 32,
        alignItems: "center",
    },
    emptyText: {
        fontSize: 14,
        color: m.colors.muted,
    },
    releaseCard: {
        marginBottom: 12,
        borderWidth: 1,
        borderColor: m.colors.border,
        borderRadius: 22,
        backgroundColor: m.colors.surface,
        overflow: "hidden",
    },
    cardHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: 14,
        backgroundColor: m.colors.surfaceAlt,
    },
    cardHeaderLeft: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        flex: 1,
    },
    versionPill: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        backgroundColor: m.colors.surface,
        borderWidth: 1,
        borderColor: m.colors.border,
        marginRight: 6,
    },
    versionPillLatest: {
        backgroundColor: BRAND.base,
        borderColor: BRAND.base,
    },
    versionText: {
        fontSize: 12,
        fontWeight: "800",
        color: m.colors.subText,
    },
    versionTextLatest: {
        color: "#FFFFFF",
    },
    latestBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: m.toneTint("success"),
        marginRight: 6,
    },
    latestText: {
        fontSize: 10,
        fontWeight: "800",
        color: m.colors.success,
    },
    dateText: {
        fontSize: 11,
        color: m.colors.muted,
    },
    cardExpandedContent: {
        padding: 14,
        borderTopWidth: 1,
        borderTopColor: m.colors.border,
    },
    groupSection: {
        marginBottom: 16,
    },
    groupHeader: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 8,
    },
    groupIconBadge: {
        width: 26,
        height: 26,
        borderRadius: 9,
        justifyContent: "center",
        alignItems: "center",
        marginRight: 8,
    },
    groupTitle: {
        fontSize: 13,
        fontWeight: "800",
    },
    itemRow: {
        flexDirection: "row",
        marginTop: 6,
        paddingLeft: 8,
    },
    dot: {
        width: 5,
        height: 5,
        borderRadius: 3,
        marginTop: 8,
        marginRight: 8,
    },
    itemContent: {
        flex: 1,
    },
    itemTitleText: {
        fontSize: 13,
        fontWeight: "700",
        color: m.colors.heading,
        lineHeight: 18,
    },
    itemDescText: {
        fontSize: 12,
        color: m.colors.subText,
        lineHeight: 17,
        marginTop: 2,
    },
    footer: {
        padding: 14,
        borderTopWidth: 1,
        borderTopColor: m.colors.border,
        flexDirection: "row",
        justifyContent: "flex-end",
        backgroundColor: m.colors.surface,
    },
    gotItBtn: {
        backgroundColor: BRAND.base,
        paddingHorizontal: 26,
        height: 46,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
    },
    gotItBtnText: {
        color: "#FFFFFF",
        fontWeight: "800",
        fontSize: 14.5,
    },
});

export default WhatsNewModal;
